import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Rnd } from "react-rnd";
import type { DraggableData, RndDragEvent, RndResizeCallback } from "react-rnd";
import { observer } from "mobx-react-lite";
import { useChatStore } from "@/hooks/useChatStore";
import { cn } from "@/lib/utils";
import type { ChatStoreInstance } from "@/stores/chat/ChatStore";
import { CORNER_Z, setChatPanelOpen, setCornerWidth } from "@/components/layout/bottomRightStack";
import { BOTTOM_RIGHT_EXCLUSION_EVENT } from "@/components/layout/bottomRightExclusion";
import { CollapsedChatBar, type CollapsedChatMode } from "./CollapsedChatBar";
import {
  BOTTOM_MARGIN,
  COLLAPSED_HEIGHT,
  COLLAPSED_WIDTH,
  TOP_OFFSET,
  bottomRightPosition,
  clampToViewport,
} from "./chatWidgetLayout";

/** Pointer travel below this many pixels is a click, not a drag. */
const DRAG_CLICK_THRESHOLD = 3;

/**
 * A user-placed widget keeps its chosen spot, but never outside the shrunken
 * viewport: the portal container is overflow-hidden, so anything beyond it is
 * clipped and unreachable.
 */
function reclampPlacedWidget(chatStore: ChatStoreInstance, isCollapsed: boolean) {
  const width = isCollapsed ? COLLAPSED_WIDTH : chatStore.widgetSize.width;
  const height = isCollapsed ? COLLAPSED_HEIGHT : chatStore.widgetSize.height;
  const clamped = clampToViewport(chatStore.widgetPosition, width, height);
  if (clamped.x !== chatStore.widgetPosition.x || clamped.y !== chatStore.widgetPosition.y) {
    chatStore.setWidgetPosition(clamped);
  }
}

/**
 * Re-anchor an auto-positioned widget to the bottom-right corner. The minimized
 * launcher has its own compact width; the panel remains resizable, so only its
 * top edge is recomputed from the live viewport height.
 */
function reanchorDockedWidget(chatStore: ChatStoreInstance, isCollapsed: boolean) {
  const liveWidth = isCollapsed ? COLLAPSED_WIDTH : chatStore.widgetSize.width;
  const desired = bottomRightPosition(liveWidth);
  chatStore.setWidgetPosition(
    isCollapsed
      ? desired
      : {
          x: desired.x,
          y: Math.max(
            0,
            window.innerHeight - TOP_OFFSET - chatStore.widgetSize.height - BOTTOM_MARGIN
          ),
        }
  );
}

interface ChatWidgetProps {
  children: React.ReactNode;
  isCollapsed: boolean;
  isExpanded: boolean;
  collapsedMode: CollapsedChatMode;
  replyPreview?: string | null;
  onExpand: () => void;
  onExpandWithDraft: (draft: string) => void;
  onOpenReply: () => void;
  /** Drop handlers for the collapsed bar, so a drag can land on it too. */
  collapsedDropProps?: Pick<
    React.HTMLAttributes<HTMLDivElement>,
    "onDragOver" | "onDragLeave" | "onDrop"
  >;
  /** A drag the chat accepts is over the collapsed bar. */
  isCollapsedDropTarget?: boolean;
}

export const ChatWidget = observer(function ChatWidget({
  children,
  isCollapsed,
  isExpanded,
  collapsedMode,
  replyPreview,
  onExpand,
  onExpandWithDraft,
  onOpenReply,
  collapsedDropProps,
  isCollapsedDropTarget = false,
}: ChatWidgetProps) {
  const chatStore = useChatStore();
  const { widgetPosition, widgetSize } = chatStore;
  const dockedWidth = isCollapsed ? COLLAPSED_WIDTH : widgetSize.width;

  // Keep the *minimized* chat pill anchored to the bottom-right corner of the
  // viewport — the standard assistant position — until the user drags it.
  // Only applies in collapsed mode; sidebar and expanded modes have their own
  // position semantics and we leave them alone.
  useEffect(() => {
    if (!isCollapsed || isExpanded || chatStore.hasUserMovedWidget) {
      // Still resolve the legacy y=-1 sentinel so it doesn't blow up Rnd.
      if (widgetPosition.y === -1) {
        chatStore.setWidgetPosition({
          x: widgetPosition.x,
          y: bottomRightPosition(dockedWidth).y,
        });
      }
      return;
    }
    const desired = bottomRightPosition(dockedWidth);
    if (widgetPosition.x !== desired.x || widgetPosition.y !== desired.y) {
      chatStore.setWidgetPosition(desired);
    }
  }, [
    isCollapsed,
    isExpanded,
    chatStore,
    chatStore.hasUserMovedWidget,
    widgetPosition.x,
    widgetPosition.y,
    dockedWidth,
    widgetSize.width,
  ]);

  // Re-anchor on viewport resize — a right-anchored widget would otherwise
  // drift off-screen when the window narrows. A user-placed widget keeps its
  // chosen spot but is still clamped back inside the shrunken viewport: the
  // portal container is overflow-hidden, so anything outside it is clipped
  // and unreachable.
  useEffect(() => {
    const onResize = () => {
      if (chatStore.hasUserMovedWidget) {
        reclampPlacedWidget(chatStore, isCollapsed);
        return;
      }
      // Fullscreen is centered relative to the nav sidebar, not anchored
      // bottom-right — re-run its own layout instead of the pill/panel math.
      if (isExpanded) {
        chatStore.refreshExpandedLayout();
        return;
      }
      reanchorDockedWidget(chatStore, isCollapsed);
    };
    window.addEventListener("resize", onResize);
    window.addEventListener(BOTTOM_RIGHT_EXCLUSION_EVENT, onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener(BOTTOM_RIGHT_EXCLUSION_EVENT, onResize);
    };
  }, [chatStore, chatStore.hasUserMovedWidget, isCollapsed, isExpanded]);

  // A user-placed widget keeps whatever position the store holds — but that
  // position may have been written against a different viewport (collapse
  // math from an out-of-bounds panel, a window resized while the widget was
  // unmounted). Clamp it back inside the container on every change; an
  // auto-anchored widget is positioned in-bounds by the effect above, and
  // clamping it too would fight the anchor's nav-sidebar minimum on very
  // narrow windows.
  useEffect(() => {
    // y === -1 is the legacy "not yet laid out" sentinel — resolved above, not clamped.
    if (isExpanded || !chatStore.hasUserMovedWidget || widgetPosition.y === -1) return;
    const width = isCollapsed ? COLLAPSED_WIDTH : widgetSize.width;
    const height = isCollapsed ? COLLAPSED_HEIGHT : widgetSize.height;
    const clamped = clampToViewport(widgetPosition, width, height);
    if (clamped.x !== widgetPosition.x || clamped.y !== widgetPosition.y) {
      chatStore.setWidgetPosition(clamped);
    }
  }, [
    chatStore,
    chatStore.hasUserMovedWidget,
    isCollapsed,
    isExpanded,
    widgetPosition,
    widgetSize.width,
    widgetSize.height,
  ]);

  // An OPEN panel owns the top of the bottom-right corner. Toasts move beside
  // its live x-position and stay below it in z-order as a collision failsafe.
  // See `layout/bottomRightStack.ts`.
  const isPanelOpen = !isCollapsed;
  useEffect(() => {
    setChatPanelOpen(isPanelOpen, widgetPosition.x);
    return () => setChatPanelOpen(false);
  }, [isPanelOpen, widgetPosition.x]);

  // The lane matches the resizable panel. The minimized launcher and Franklin
  // update cards are deliberately narrower and right-aligned within that
  // column. Fullscreen is not a corner width — leave the last docked one set.
  useEffect(() => {
    if (isExpanded) return;
    setCornerWidth(widgetSize.width);
  }, [isExpanded, widgetSize.width]);

  // Track expand/collapse transitions so the CSS transition is only applied
  // during mode changes, not during normal drag operations (WHA-2404).
  const [isAnimating, setIsAnimating] = useState(false);
  const prevExpandedRef = useRef(isExpanded);
  useEffect(() => {
    if (prevExpandedRef.current !== isExpanded) {
      prevExpandedRef.current = isExpanded;
      setIsAnimating(true);
      const timer = setTimeout(() => setIsAnimating(false), 200);
      return () => clearTimeout(timer);
    }
  }, [isExpanded]);

  // react-draggable fires onStart/onStop for a plain click too (mousedown +
  // mouseup with no travel), and the browser fires a click on the drop target
  // after every drag's mouseup. Only a real drag may pin the position, and a
  // real drag must NOT also read as a click — releasing one over the pill
  // would otherwise open the panel.
  //
  // Travel is accumulated from `onDrag` deltas rather than measured between
  // onDragStart and onDragStop: react-rnd adds `offsetFromParent` to x/y in
  // onDrag/onDragStop but NOT in onDragStart (react-rnd lib/index.js), so
  // start-vs-stop compares two different coordinate spaces. deltaX/deltaY
  // pass through untouched on the default "both" axis.
  const dragTravelRef = useRef(0);
  const suppressClickRef = useRef(false);

  const handleDragStart = useCallback(() => {
    dragTravelRef.current = 0;
  }, []);

  const handleDrag = useCallback((_e: RndDragEvent, d: DraggableData) => {
    dragTravelRef.current += Math.hypot(d.deltaX, d.deltaY);
  }, []);

  const handleDragStop = useCallback(
    (_e: RndDragEvent, d: DraggableData) => {
      const travelled = dragTravelRef.current;
      dragTravelRef.current = 0;
      if (travelled < DRAG_CLICK_THRESHOLD) return; // a click, not a drag
      chatStore.setWidgetPosition({ x: d.x, y: d.y });
      // A drag means the user has chosen a position — stop auto-anchoring to
      // the bottom-right corner until the page reloads.
      chatStore.setUserMovedWidget(true);
      // Swallow the click the browser fires right after this mouseup. The
      // flag is also cleared on the next mousedown, so a drag whose click
      // never lands on the widget can't disarm the following real click.
      suppressClickRef.current = true;
    },
    [chatStore]
  );

  // Capture-phase, so the post-drag click dies before PillShell's onClick
  // (or the reply chevron) can treat it as "open the chat".
  const handleClickCapture = useCallback((e: React.MouseEvent) => {
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      e.preventDefault();
      e.stopPropagation();
    }
  }, []);

  // A fresh press means the previous drag's click is never coming — clear the
  // flag so this press's own click is not swallowed.
  const handleMouseDownCapture = useCallback(() => {
    suppressClickRef.current = false;
  }, []);

  const handleResizeStop: RndResizeCallback = useCallback(
    (_e, _direction, ref, _delta, position) => {
      chatStore.setWidgetSize({
        width: ref.offsetWidth,
        height: ref.offsetHeight,
      });
      chatStore.setWidgetPosition({ x: position.x, y: position.y });
    },
    [chatStore]
  );

  const widget = isCollapsed ? (
    <Rnd
      position={widgetPosition}
      size={{ width: COLLAPSED_WIDTH, height: COLLAPSED_HEIGHT }}
      onDragStart={handleDragStart}
      onDrag={handleDrag}
      onDragStop={handleDragStop}
      dragHandleClassName="chat-drag-handle"
      enableResizing={false}
      bounds="parent"
      style={{ zIndex: 40, pointerEvents: "auto" }}
    >
      <div
        className={cn(
          "relative h-full w-full rounded-[24px]",
          isCollapsedDropTarget && "ring-2 ring-primary ring-offset-2"
        )}
        data-chat-drop-target={isCollapsedDropTarget || undefined}
        onClickCapture={handleClickCapture}
        onMouseDownCapture={handleMouseDownCapture}
        {...collapsedDropProps}
      >
        <CollapsedChatBar
          mode={collapsedMode}
          replyPreview={replyPreview}
          onExpand={onExpand}
          onExpandWithDraft={onExpandWithDraft}
          onOpenReply={onOpenReply}
        />
      </div>
    </Rnd>
  ) : (
    <Rnd
      position={widgetPosition}
      size={widgetSize}
      onDragStart={handleDragStart}
      onDrag={handleDrag}
      onDragStop={handleDragStop}
      onResizeStop={handleResizeStop}
      dragHandleClassName="chat-drag-handle"
      minWidth={300}
      minHeight={360}
      bounds="parent"
      style={{
        zIndex: 40,
        pointerEvents: "auto",
        transition: isAnimating
          ? "width 200ms ease, height 200ms ease, transform 200ms ease"
          : undefined,
      }}
    >
      <div className="flex h-full w-full flex-col overflow-hidden rounded-[10px] border border-neutral-200 bg-white shadow-[0px_8px_24px_rgba(0,0,0,0.15),0px_4px_8px_rgba(0,0,0,0.1)]">
        {children}
      </div>
    </Rnd>
  );

  return createPortal(
    <div
      className="pointer-events-none fixed inset-x-0 bottom-0 overflow-hidden"
      style={{
        top: TOP_OFFSET,
        zIndex: isPanelOpen ? CORNER_Z.chatOpen : CORNER_Z.chatCollapsed,
      }}
    >
      {widget}
    </div>,
    document.body
  );
});

ChatWidget.displayName = "ChatWidget";
